import { featuresTypes } from '@mfe-4cs/defs/featureTypes';
import { TefComs } from '@mfe-4cs/types/typings';
import { CustomerAttribute, CustomerAttributeGroup } from '@mfec/customerAttributes/types';

export interface ConsentChanges {
  granted: number;
  revoked: number;
}

export interface ConsentAndCustomerAttributeChangesOptions {
  consentsA: TefComs.ConsentStatus[];
  consentsB: TefComs.ConsentStatus[];
  customerAttributesA: CustomerAttribute[];
  customerAttributesB: CustomerAttribute[];
}

//
// Detects changes between two sets of consents
// consentB is expected to be the updated consent list
//
// ONLY looks at consent.granted and completely ignores conent features like "dataTypes", "channels", ...
//
export const consentChanges = (
  consentsA : TefComs.ConsentStatus[],
  consentsB: TefComs.ConsentStatus[],
): ConsentChanges => {
  let consentB = null;

  const changes : ConsentChanges = {
    granted: 0,
    revoked: 0,
  };

  consentsA.forEach((consentA) => {
    consentB = consentsB.find((consB) => consentA.id === consB.id);
    if (consentB) {
      if (consentA.granted === consentB.granted) return;
      if (consentB.granted === true) {
        changes.granted += 1;
      } else {
        changes.revoked += 1;
      }
    }
  });
  return changes;
};

//
// Detects changes  between two sets of customer attributes
// consentB is expected to be the updated customer attribute list
//
export const customerAttributesChanges = (
  cattrsA: CustomerAttribute[],
  cattrsB: CustomerAttribute[],
) : ConsentChanges => {
  const changes : ConsentChanges = {
    granted: 0,
    revoked: 0,
  };

  cattrsA.forEach((cattrA) => {
    const cattrB = cattrsB.find((cB) => cB.id === cattrA.id);
    if (cattrB) {
      ['standard', 'extra'].forEach((itemGroup) => {
        const cattrAItems = (cattrA[itemGroup] as CustomerAttributeGroup).items;
        const cattrBItems = (cattrB[itemGroup] as CustomerAttributeGroup).items;

        cattrAItems.forEach((cattrAItem) => {
          // eslint-disable-next-line sonarjs/no-nested-functions
          const cattrBItem = cattrBItems.find((cB) => cB.id === cattrAItem.id);
          if (cattrBItem) {
            if (cattrAItem.granted === cattrBItem.granted) return;
            if (cattrBItem.granted === true) {
              changes.granted += 1;
            } else {
              changes.revoked += 1;
            }
          }
        });
      });
    }
  });

  return changes;
};

//
// Compares the set features of two lists of ConsentStatus
//
export const featureChanges = (
  consentsA : TefComs.ConsentStatus[],
  consentsB: TefComs.ConsentStatus[],
): ConsentChanges => {
  let consentB: TefComs.ConsentStatus | undefined;

  const changes : ConsentChanges = {
    granted: 0,
    revoked: 0,
  };

  consentsA.forEach((consentA) => {
    consentB = consentsB.find((consB) => consentA.id === consB.id);
    if (consentB) {
      featuresTypes.forEach((featureType) => {
        const featuresA = consentA[featureType] ?? [];
        const featuresB = consentB![featureType] ?? [];
        featuresB.forEach((featureB) => {
          // eslint-disable-next-line sonarjs/no-nested-functions
          const featureA = featuresA.find((feature) => feature.id === featureB.id);
          if (featureA?.granted === featureB.granted) return;
          if (featureB.granted === true) {
            changes.granted += 1;
          } else {
            changes.revoked += 1;
          }
        });
      });
    }
  });
  return changes;
};

//
// Compares two sets of consents and customer attributes
//
// Compares:
// - consents (consent.granted)
// - features (consent[featureType]
// - customerAttributes
//
export const consentAndCustomerAttributeChanges = ({
  consentsA, consentsB, customerAttributesA, customerAttributesB,
} : ConsentAndCustomerAttributeChangesOptions) : ConsentChanges => {
  const consentDiff = consentChanges(consentsA, consentsB);
  const featureDiff = featureChanges(consentsA, consentsB);
  const customerAttributeDiff = customerAttributesChanges(customerAttributesA, customerAttributesB);
  return {
    granted: consentDiff.granted + customerAttributeDiff.granted + featureDiff.granted,
    revoked: consentDiff.revoked + customerAttributeDiff.revoked + featureDiff.revoked,
  };
};
